import {
  Modal,
  IconButton,
  ButtonSize,
  Button,
  ButtonVariant,
  IconButtonVariant,
  IconButtonSize
} from "shared/components";
import { XClose } from "untitledui-js-base";
import { AsyncSelect } from "../../components/AsyncSelect/AsyncSelect";
import {
  loadPropertyOptions,
  loadUnitOptions
} from "../../../api/landlord/asyncUnitProperties";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "../../../api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/error";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

export const MoveModal = ({
  onClose,
  applicationId
}: {
  onClose: () => void;
  applicationId: string;
}) => {
  const { Unit, unit, Property } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState<
    number | undefined
  >();
  const [selectedUnitId, setSelectedUnitId] = useState<number | undefined>();

  const handleClose = () => {
    setSelectedPropertyId(undefined);
    setSelectedUnitId(undefined);
    onClose();
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      addressApi.updateApplicationUnit(applicationId, Number(selectedUnitId)),
    onSuccess: () => {
      toast.success("Application successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });
      handleClose();
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={handleClose}
      panelClassName="max-w-[600px] w-full"
    >
      <div className="p-6 border-b border-alpha-100">
        <div className="flex items-center justify-between">
          <p className="m-0 text-xl font-bold leading-8">
            Move application to another {unit}
          </p>
          <IconButton
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.SM}
            borderLess
            className="!justify-end translate-x-2"
            onClick={() => {
              handleClose();
            }}
            Icon={XClose}
          />
        </div>

        <p className="mb-0 mt-2 text-sm text-content-400 max-w-[calc(100%-40px)]">
          Application will be moved to the selected {unit}. Application will use
          the screening settings from the selected {unit}. Individual
          applications will keep the original set of requested information, but
          any missing reports will be added
        </p>
      </div>

      <div className="py-8 px-6 flex items-start gap-4 bg-background-900">
        <div className="w-full">
          <AsyncSelect
            id="property"
            label={Property}
            value={selectedPropertyId || ""}
            searchable={true}
            hideOptional
            // @ts-expect-error -- TODO: fix this
            loadOptions={loadPropertyOptions}
            error={propertyError}
            options={[]}
            onChange={(e) => {
              if (!Array.isArray(e)) {
                setSelectedPropertyId(Number(e.target.value));
                setPropertyError("");
                setSelectedUnitId(undefined);
              }
            }}
            additional={{
              page: 1
            }}
          />
        </div>
        <div className="w-full">
          <AsyncSelect
            id="unit"
            label={Unit}
            key={selectedPropertyId}
            searchable={true}
            hideOptional
            value={selectedUnitId || ""}
            options={[]}
            // @ts-expect-error -- TODO: fix this
            loadOptions={loadUnitOptions}
            error={unitError}
            additional={{ page: 1, propertyId: selectedPropertyId }}
            onChange={(e) => {
              if (!Array.isArray(e)) {
                setSelectedUnitId(Number(e.target.value));
                setUnitError("");
              }
            }}
          />
        </div>
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button className="w-[103px]" size={ButtonSize.SM} onClick={onClose}>
          Back
        </Button>
        <Button
          className="w-[130px]"
          size={ButtonSize.SM}
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            if (selectedUnitId) mutate();
          }}
          loading={isPending}
          disabled={!selectedUnitId || isPending}
        >
          Confirm
        </Button>
      </div>
    </Modal>
  );
};
